import "./App.css";
import { CreateContentPlan } from "./pages/ContentPlan/CreateContentPlan";
import { CreateNewPassword } from "./pages/ForgotPassword/CreateNewPassword";
import { SendPassword } from "./pages/ForgotPassword/SendPassword";
import HomePage from "./pages/Home/NewHomePage";
import SocialsPage from "./pages/Socials";
import {PersonalBrandStrategy} from "./pages/PersonalBrandStrategy/PersonalBrandStrategy";
import SignInPage from "./pages/SignIn";
import SignupPage from "./pages/SignUp";
import NotFoundPage from "./pages/NotFound/NotFound";

import {
    Routes,
    Route,
    Navigate,
    useNavigate,
    useLocation,
} from "react-router-dom";
import {fetchWrapper, history} from "./helpers";
import {useSelector, useDispatch} from "react-redux";
import {useEffect, useRef, useState} from "react";
import {authActions, strategiesActions} from "./store";
import {Post} from "./pages/CreatePost/Posts";
import Oauth from "./pages/Oauth";
import {message, Spin} from "antd";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import i18n from "i18next";
import {useTranslation, initReactI18next} from "react-i18next";
import localeEn from "./locale/locale.en";
import localeRu from "./locale/locale.ru";
import localeKk from "./locale/locale.kk";
import PrivateRoute from "./components/PrivateRouter";
import {ProfilePage} from "./pages/Profile";
import {SubscriptionPlansPage} from "./pages/SubscriptionPlans";
import {Posts} from "./pages/Posts/Posts";
import {GeneratePost} from "./pages/GeneratePost/GeneratePost";
import {HowWeWorkPage} from "./pages/HowWeWork/HowWeWorkPage";
import SurveyIndustryAndPositions from "./pages/Surveys/SurveyIndustryAndPositions";
import AssistantPage from "./pages/Assistant/AssistantPage";
import PersonalityExperience from "./pages/Surveys/PersonalityExperience";
import Goals from "./pages/Surveys/Goals";
import Support from "./pages/Support/Support";
import PdfViewer from "./pages/PDFViewer/PDFViewer";
import Guideline from "./pages/Guideline/Guideline";
import {useInviteTokenInterceptor} from "./utils/invite-token-interceptor";
import HowToWorkService from "./pages/Introductions/HowToWorkService";
import HTWSPostPage from "./pages/Introductions/HowToWorkService/HTWSPostPage";
import HTWSPostCreate from "./pages/Introductions/HowToWorkService/HTWSPostCreate";
import VerifyEmail from "./pages/VerifyEmail";
import ModerationPage from "./pages/Moderation/ModerationPage";
import CalendarPage from "./pages/Calendar/CalendarPage";
import AlertsManagementPage from "./pages/AlertsManagement/AlertsManagementPage";
import AchievementsPage from "./pages/Achievements/AchievementsPage";
import DistributePostPage from "./pages/DistributePost/DistributePostPage";
import ViewDistributedPosts from "./pages/DistributePost/ViewDistributedPosts";
import { detectedInitialLang, setAppLanguage } from "./utils/LangUtils/langStorage";
import PostComposerPage from "./pages/WorkInProgressPages/PostComporserPreview";
import NotFound from "./pages/NotFound/NotFound";
import AnalyticsPage from "./pages/Analytics/AnalyticsPage";
import MediaInsightsPage from "./pages/Analytics/MediaInsightsPage";

i18n.use(initReactI18next).init({
    resources: {
        en: {
            translation: localeEn,
        },
        ru: {
            translation: localeRu,
        },
        kk: {
            translation: localeKk,
        },
    },
    lng: "ru",
    fallbackLng: "en",

    interpolation: {
        escapeValue: false,
    },
});

dayjs.extend(utc);

const baseUrl = `${process.env.REACT_APP_API_URL}`;

setAppLanguage(detectedInitialLang());

function App() {
    const [isLoading, setIsLoading] = useState(true);
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [language, setLanguage] = useState();
    const authUser = useSelector((obj) => obj.auth.user);
    // const socketRef = useRef(); // Socket:DEPRECATED
    // const [sendSocket, setSendSocket] = useState(); // Socket:DEPRECATED
    useInviteTokenInterceptor(baseUrl);

    const generatingStrategy = useSelector((obj) => obj.strategy.generating);
    const interval = useRef(null);

    history.navigate = useNavigate();
    history.location = useLocation();

    // Socket:DEPRECATED
    // useEffect(() => {
    //   const socket = io.connect(process.env.BASE_DOMAIN);
    //   socketRef.current = socket;

    //   setSendSocket(socket);
    //   return () => {
    //     socket.disconnect();
    //     socketRef.current = false;
    //     setSendSocket(null);
    //   };
    // }, []);

    const refreshToken = () => {
        fetchWrapper.get(`${baseUrl}/auth/refresh-token`).then((res) => {
            const oldUser = JSON.parse(localStorage.getItem("user"));
            oldUser.languageGeneration = language;
            oldUser.accessToken = res.accessToken;
            localStorage.setItem("user", JSON.stringify(oldUser));
            dispatch(authActions.updateUser());
        });
    };

    function clearAndRefreshCache() {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(registration => {
                registration.active.postMessage({type: 'CLEAR_CACHE'});
                localStorage.setItem('cacheHasRefreshed', new Date().toISOString());
                window.location.pathname = '/';
            }).catch((error) => {
                message.error(`${t("functions.error:")} ${error.message ? error.message : error}`);
            });
        }
    }

    async function userInit() {
        setIsLoading(true);
        await fetchWrapper.get(`${baseUrl}/profile/`).then((res) => {
            setLanguage(res.languageGeneration);
            if (!res?.emailVerified) {
                navigate('/verify-email');
            } else {
                if (window.location.pathname.includes('/verify-email')) {
                    navigate('/');
                }
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }

    useEffect(() => {
        userInit()
    }, [authUser]);

    useEffect(() => {
        refreshToken();
    }, [language]);

    useEffect(() => {
        if (authUser) {
            dispatch(strategiesActions.getStrategy());
        }
    }, [authUser]);

    useEffect(() => {
        if (generatingStrategy) {
            interval.current = setInterval(() => {
                dispatch(strategiesActions.getStrategy());
            }, 5000);
        } else {
            clearInterval(interval.current);
            interval.current = null;
        }

        return () => {
            clearInterval(interval.current);
        };
    }, [dispatch, generatingStrategy, interval]);

    // Socket:DEPRECATED
    // useEffect(() => {
    //   if (socketRef.current && authUser) {
    //     socketRef.current.on("connect", function () {
    //       socketRef.current.emit("subscribe", authUser.id);
    //     });

    //     socketRef.current.on("finishedStrategy", () => {
    //       dispatch(strategiesActions.getStrategy());
    //     });
    //   }
    // }, [socketRef.current, authUser]);

    useEffect(() => {
        const { pathname } = window.location;
        const differenceTime = Number(((Date.now() - new Date(localStorage.getItem('cacheHasRefreshed')).getTime()) / 1000 / 60).toFixed());

        const isOnOAuthCallback = pathname.startsWith('/oauth'); // << ключевая правка

        if (!isOnOAuthCallback && (differenceTime > 240 || !localStorage.getItem('cacheHasRefreshed'))) {
            localStorage.setItem('cacheHasRefreshed', new Date().toISOString());
            clearAndRefreshCache();
        }
    }, []);

    if (isLoading) return (
        <div className="flex justify-center items-center w-full h-screen">
            <Spin size="large"/>
        </div>
    );

    return (
        <Routes>
            <Route
                path="/"
                element={<Navigate to={authUser ? "home" : "/sign-in"}/>}
            />
            <Route path="/sign-up" element={<SignupPage/>}/>
            <Route path="/document/:document" element={<PdfViewer/>}/>
            <Route path="/sign-in" element={<SignInPage/>}/>
            <Route path="/not-found-page" element={<NotFoundPage/>}/>

            <Route path="/how-to-work" element={<HowToWorkService/>}/>
            <Route path="/how-to-work-post/create" element={<HTWSPostCreate/>}/>
            <Route path="/how-to-work-post/:id" element={<HTWSPostPage/>}/>

            <Route path="/forgot-password" element={<SendPassword/>}/>
            <Route path="/new-password" element={<CreateNewPassword/>}/>
            <Route path="/oauth" element={<Oauth/>}/>

            <Route element={<PrivateRoute/>}>

                <Route path="/verify-email" element={<VerifyEmail/>}/>
                {/*<Route path="/welcome" element={<WelcomePage />} />*/}
                <Route path="/profile" element={<ProfilePage/>}/>
                <Route path="/survey" element={<SurveyIndustryAndPositions/>}/>
                <Route path="/personality-survey" element={<PersonalityExperience/>}/>
                <Route path="/goals-survey" element={<Goals/>}/>
                {/*<Route path="/survey-legacy" element={<SurveyPage />} />*/}
                <Route path="/support" element={<Support/>}/>
                <Route path="/guideline" element={<Guideline/>}/>
                <Route path="/home" element={<HomePage/>}/>
                <Route path="/assistant" element={<AssistantPage/>}/>
                <Route path="/socials" element={<SocialsPage/>}/>
                <Route path="/personal-brand" element={<PersonalBrandStrategy/>}/>
                <Route path="/moderation" element={<ModerationPage/>}/>
                <Route path="/alerts-management" element={<AlertsManagementPage/>}/>
                <Route path="/content-plan" element={<CreateContentPlan/>}/>
                <Route path="/create-post/:contentId" element={<Post/>}/>
                <Route path="/posts/" element={<Posts/>}/>
                <Route path="/generate-post/" element={<GeneratePost/>}/>
                <Route path="/subscription-plans" element={<SubscriptionPlansPage/>}/>
                <Route path="/how-we-work" element={<HowWeWorkPage/>}/>
                <Route path="/publication-calendar" element={<CalendarPage/>}/>
                <Route path="/achievements" element={<AchievementsPage/>}/>
                <Route path="/post-distribution" element={<DistributePostPage/>}/>
                <Route path='/view-post-dist' element={<ViewDistributedPosts/>}/>
                <Route path='/post-composer' element={<PostComposerPage/>}/>
                <Route path="/analytics" element={<AnalyticsPage/>}/>
                <Route path="/analytics/media/:mediaId" element={<MediaInsightsPage/>}/>
            </Route>
        </Routes>
    );
}

export default App;
